/**
 * CheckoutSuccessPage - Payment verification and success confirmation
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Check, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/shared/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setVerified(true);
        setLoading(false);
        
        if (response.data.metadata && response.data.metadata.relationship_id) {
          sessionStorage.setItem('relationship_id', response.data.metadata.relationship_id);
          sessionStorage.setItem('selected_level', response.data.metadata.level);
        }
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleContinue = () => {
    navigate('/intake');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center" style={{minHeight: 'calc(100vh - 100px)'}}>
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-stone-900 mx-auto mb-4" />
            <p className="text-stone-600">Verifying your payment...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!verified) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center p-6" style={{minHeight: 'calc(100vh - 100px)'}}>
          <div className="max-w-lg w-full text-center">
            <p className="text-stone-600">Payment could not be verified. Please contact support.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex items-center justify-center p-6" style={{minHeight: 'calc(100vh - 100px)'}}>
        <div className="max-w-lg w-full bg-white rounded-2xl border border-stone-100 shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-mc-blue-deep" />
          </div>
          
          <h2 className="text-3xl font-bold text-stone-900 mb-4" data-testid="payment-success-heading">
            Payment Successful
          </h2>
          
          <p className="text-stone-600 leading-relaxed mb-8">
            Thank you! Your MarrsingClause® experience is ready to begin.
          </p>

          <button
            onClick={handleContinue}
            className="bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
            data-testid="continue-to-intake"
          >
            Continue to Intake
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
